'use client'

import * as React from 'react'
import { cn } from '../../../lib/utils'
import { Button } from '../Button'
import { Icon } from '../Icon'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../Dialog'

export interface MigrationImpact {
  operationType: string
  resourceType: string
  resourceHandle: string
  affectedRecords?: number
  message?: string
  isDestructive?: boolean
}

export interface MigrationApprovalAlertProps {
  migrationId: string
  title?: string
  description?: string
  impacts: MigrationImpact[]
  timeoutMinutes?: number
  createdAt?: Date
  onApprove: (migrationId: string) => void
  onDeny: (migrationId: string) => void
  className?: string
}

export const MigrationApprovalAlert: React.FC<MigrationApprovalAlertProps> = ({
  migrationId,
  title = 'Migration Requires Approval',
  description = 'This migration contains destructive operations that require your approval before proceeding.',
  impacts,
  timeoutMinutes = 30,
  createdAt,
  onApprove,
  onDeny,
  className,
}) => {
  const [showDetails, setShowDetails] = React.useState(false)
  const [timeRemaining, setTimeRemaining] = React.useState<string>('')

  React.useEffect(() => {
    if (!createdAt) return

    const updateTimer = () => {
      const now = new Date()
      const expiresAt = new Date(
        createdAt.getTime() + timeoutMinutes * 60 * 1000,
      )
      const diff = expiresAt.getTime() - now.getTime()

      if (diff <= 0) {
        setTimeRemaining('Expired')
        return
      }

      const minutes = Math.floor(diff / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)
      setTimeRemaining(`${minutes}m ${seconds}s`)
    }

    updateTimer()
    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [createdAt, timeoutMinutes])

  const destructiveImpacts = impacts.filter((i) => i.isDestructive)
  const totalAffectedRecords = destructiveImpacts.reduce(
    (sum, i) => sum + (i.affectedRecords || 0),
    0,
  )

  return (
    <>
      <div
        className={cn(
          'relative w-full rounded-lg border border-amber-500/50 bg-amber-50 dark:bg-amber-950/20 px-4 py-3 text-sm',
          className,
        )}
        role="alert"
      >
        <div className="flex flex-row justify-between items-start gap-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-5 h-5 flex items-center justify-center mt-0.5">
              <Icon name="TriangleAlert" className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h5 className="font-medium leading-none tracking-tight text-amber-800 dark:text-amber-200">
                {title}
              </h5>
              <p className="mt-1 text-amber-700 dark:text-amber-300">
                {description}
              </p>
              <div className="mt-2 flex items-center gap-4 text-xs text-amber-600 dark:text-amber-400">
                <span>
                  {destructiveImpacts.length} destructive operation
                  {destructiveImpacts.length !== 1 ? 's' : ''}
                </span>
                {totalAffectedRecords > 0 && (
                  <span>
                    {totalAffectedRecords.toLocaleString()} records affected
                  </span>
                )}
                {timeRemaining && (
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" className="w-3 h-3" />
                    {timeRemaining}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowDetails(true)}
                className="mt-2 text-xs text-amber-700 dark:text-amber-300 underline hover:no-underline"
              >
                View details
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              label="Deny"
              handleClick={() => onDeny(migrationId)}
              className="border-amber-500 text-amber-700 hover:bg-amber-100"
            />
            <Button
              variant="default"
              size="sm"
              label="Approve"
              handleClick={() => onApprove(migrationId)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            />
          </div>
        </div>
      </div>

      <MigrationApprovalDialog
        open={showDetails}
        onOpenChange={setShowDetails}
        migrationId={migrationId}
        impacts={impacts}
        timeRemaining={timeRemaining}
        onApprove={() => {
          setShowDetails(false)
          onApprove(migrationId)
        }}
        onDeny={() => {
          setShowDetails(false)
          onDeny(migrationId)
        }}
      />
    </>
  )
}

interface MigrationApprovalDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  migrationId: string
  impacts: MigrationImpact[]
  timeRemaining?: string
  onApprove: () => void
  onDeny: () => void
}

export const MigrationApprovalDialog: React.FC<
  MigrationApprovalDialogProps
> = ({
  open,
  onOpenChange,
  migrationId,
  impacts,
  timeRemaining,
  onApprove,
  onDeny,
}) => {
  const destructiveImpacts = impacts.filter((i) => i.isDestructive)
  const nonDestructiveImpacts = impacts.filter((i) => !i.isDestructive)

  const getOperationIcon = (operationType: string): string => {
    if (operationType.startsWith('DELETE')) return 'Trash2'
    if (operationType.startsWith('CREATE')) return 'Plus'
    if (operationType.startsWith('UPDATE')) return 'Pencil'
    return 'Circle'
  }

  const getResourceIcon = (resourceType: string): string => {
    switch (resourceType) {
      case 'MODEL':
        return 'Database'
      case 'FIELD':
        return 'Columns3'
      case 'RELATIONSHIP':
        return 'Link'
      case 'FIELD_DEFINITION':
        return 'Settings'
      case 'FIELD_DEFINITION_OPTION':
        return 'List'
      default:
        return 'Circle'
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon name="TriangleAlert" className="w-5 h-5 text-amber-600" />
            Migration Approval Required
          </DialogTitle>
          <DialogDescription>
            Review the following changes before approving this migration.
            {timeRemaining && (
              <span className="ml-2 text-amber-600">
                Time remaining: {timeRemaining}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {destructiveImpacts.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2 flex items-center gap-2">
                <Icon name="CircleAlert" className="w-4 h-4" />
                Destructive Operations ({destructiveImpacts.length})
              </h4>
              <div className="space-y-2">
                {destructiveImpacts.map((impact, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800"
                  >
                    <Icon
                      name={
                        getOperationIcon(impact.operationType) as
                          | 'Trash2'
                          | 'Plus'
                          | 'Pencil'
                          | 'Circle'
                      }
                      className="w-4 h-4 text-red-600 mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon
                          name={
                            getResourceIcon(impact.resourceType) as
                              | 'Database'
                              | 'Columns3'
                              | 'Link'
                              | 'Settings'
                              | 'List'
                              | 'Circle'
                          }
                          className="w-3 h-3 text-red-500"
                        />
                        <span className="text-sm font-medium text-red-800 dark:text-red-200">
                          {impact.operationType.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-red-600 dark:text-red-400">
                          {impact.resourceHandle || impact.resourceType}
                        </span>
                      </div>
                      {impact.message && (
                        <p className="mt-1 text-xs text-red-700 dark:text-red-300">
                          {impact.message}
                        </p>
                      )}
                      {impact.affectedRecords !== undefined &&
                        impact.affectedRecords > 0 && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400 font-medium">
                            {impact.affectedRecords.toLocaleString()} records
                            will be affected
                          </p>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {nonDestructiveImpacts.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2 flex items-center gap-2">
                <Icon name="Info" className="w-4 h-4" />
                Other Operations ({nonDestructiveImpacts.length})
              </h4>
              <div className="space-y-2">
                {nonDestructiveImpacts.map((impact, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700"
                  >
                    <Icon
                      name={
                        getOperationIcon(impact.operationType) as
                          | 'Trash2'
                          | 'Plus'
                          | 'Pencil'
                          | 'Circle'
                      }
                      className="w-4 h-4 text-gray-600 mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Icon
                          name={
                            getResourceIcon(impact.resourceType) as
                              | 'Database'
                              | 'Columns3'
                              | 'Link'
                              | 'Settings'
                              | 'List'
                              | 'Circle'
                          }
                          className="w-3 h-3 text-gray-500"
                        />
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {impact.operationType.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {impact.resourceHandle || impact.resourceType}
                        </span>
                      </div>
                      {impact.message && (
                        <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
                          {impact.message}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <p className="text-xs text-gray-500">
              Migration ID: {migrationId.slice(0, 8)}...
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                label="Deny Migration"
                handleClick={onDeny}
              />
              <Button
                variant="destructive"
                label="Approve Migration"
                handleClick={onApprove}
                className="bg-amber-600 hover:bg-amber-700"
              />
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MigrationApprovalAlert
